// Form Submission Context
import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {formSubmissionService} from '../services/form-submission.service';
import {evaluateConditions} from '../utils/conditionalLogicUtils';
import {ValidationService} from '../utils/validation';
import {hasSubmittedForm, markFormAsSubmitted} from '../utils/submissionTracking';
import {useConditionalLogic} from '../hooks/useConditionalLogic';
import type {
  FormNavigationState,
  FormPageData,
  FormProgress,
  FormSubmissionData,
  FormValidationError,
  Integration,
  PublicFormData,
  QuizResults,
  SubmissionResponse
} from '../types/form-submission.types';
import type {FormField} from '../types/form-builder.types';

interface FormSubmissionContextValue {
  form: PublicFormData | null;
  formData: Record<string, any>;
  errors: Record<string, FormValidationError>;
  currentPage: number;
  pages: FormPageData[];
  progress: FormProgress;
  navigation: FormNavigationState;
  isSubmitting: boolean;
  isLoading: boolean;
  loadError: string | null;
  submissionComplete: boolean;
  submissionResponse: SubmissionResponse | null;
  quizResults: QuizResults | null;
  startTime: Date | null;
  integrations: Integration[];
  isDraftLoading: boolean;
  isDraftSaving: boolean;

  // Actions
  setFieldValue: (fieldId: string, value: any) => void;
  validateField: (fieldId: string) => Promise<boolean>;
  validatePage: (pageIndex: number) => Promise<boolean>;
  goToPage: (pageIndex: number) => void;
  nextPage: () => Promise<void>;
  previousPage: () => void;
  submitForm: () => Promise<void>;
  saveDraft: () => Promise<void>;
  resetForm: () => void;
  loadForm: (formId: number | string) => Promise<void>;
  getIntegration: (provider: string) => Integration | null;
  getIntegrationConfig: (provider: string) => any | null;
}

const FormSubmissionContext = createContext<FormSubmissionContextValue | undefined>(undefined);

export function FormSubmissionProvider({ children }: { children: ReactNode }) {
  const [form, setForm] = useState<PublicFormData | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, FormValidationError>>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [submissionResponse, setSubmissionResponse] = useState<SubmissionResponse | null>(null);
  const [quizResults, setQuizResults] = useState<QuizResults | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [visitedPages, setVisitedPages] = useState<Set<number>>(new Set([0]));
  const [pendingSubmissionId, setPendingSubmissionId] = useState<number | null>(null); // Track pending submission for payment retry
  const [_draftId, setDraftId] = useState<string | null>(null); // Track draft ID for save & continue (underscore prefix = intentionally unused, only setter used)
  const [isDraftLoading, setIsDraftLoading] = useState(false); // Track draft loading state
  const [isDraftSaving, setIsDraftSaving] = useState(false); // Track draft saving state

  // Integrations from form data
  const integrations = useMemo(() => form?.integrations || [], [form?.integrations]);

  // Use ref to track loading state across re-renders and prevent duplicate calls
  const loadingRef = useRef<string | number | null>(null);
  const loadedFormIdRef = useRef<string | number | null>(null);

  // Memoized field lookup map for performance
  const fieldMap = useMemo(() => {
    const map = new Map<string, FormField>();
    if (form?.sections) {
      form.sections.forEach(section => {
        section.rows?.forEach(row => {
          row.fields?.forEach(field => {
            map.set(field.id, field);
          });
        });
      });
    }
    return map;
  }, [form]);

  // Get all fields for conditional logic evaluation
  const allFields = useMemo(() => {
    const fields: FormField[] = [];
    if (form?.sections) {
      form.sections.forEach(section => {
        section.rows?.forEach(row => {
          row.fields?.forEach(field => {
            fields.push(field);
          });
        });
      });
    }
    return fields;
  }, [form]);

  // Apply conditional logic to get hidden fields
  const conditionalLogic = useConditionalLogic(
    form?.conditionalRules || [],
    formData,
    allFields
  );

  // Clear errors for hidden fields when conditional logic changes
  useEffect(() => {
    if (conditionalLogic.hiddenFields.size > 0) {
      setErrors(prev => {
        const newErrors = { ...prev };
        let hasChanges = false;

        // Remove errors for any hidden fields
        conditionalLogic.hiddenFields.forEach(fieldId => {
          if (newErrors[fieldId]) {
            delete newErrors[fieldId];
            hasChanges = true;
          }
        });

        // Only update state if there were actual changes
        return hasChanges ? newErrors : prev;
      });
    }
  }, [conditionalLogic.hiddenFields]);

  // Load form data
  const loadForm = useCallback(async (formId: number | string) => {
    // Prevent duplicate calls for the same form
    if (loadingRef.current === formId || loadedFormIdRef.current === formId) {
      console.log('Form already loading or loaded, skipping duplicate call');
      return;
    }

    try {
      loadingRef.current = formId;
      setIsLoading(true);
      setLoadError(null);

      // Extract token from URL if present
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      const formData = typeof formId === 'string' && isNaN(Number(formId))
        ? await formSubmissionService.getPublicFormByAlias(formId)
        : await formSubmissionService.getPublicForm(Number(formId), token || undefined);

      setForm(formData);
      setStartTime(new Date());
      loadedFormIdRef.current = formId;

      // Client-side check: If multiple submissions not allowed and user has already submitted
      // This provides immediate feedback even before server validation
      if (!formData.alreadySubmitted &&
          formData.settings?.allowMultipleSubmissions === false &&
          hasSubmittedForm(formData.id)) {
        // Mark as already submitted for consistent UI
        setForm({
          ...formData,
          alreadySubmitted: true,
          message: 'You have already submitted this form and multiple submissions are not allowed.',
        });
        return;
      }

      // Load saved draft if Save & Continue is enabled
      let draftLoaded = false;
      if (formData.settings?.allowSaveAndContinue && !formData.alreadySubmitted) {
        // First check localStorage for draft ID (fast path)
        const savedDraftId = localStorage.getItem(`easyforms_draft_${formData.id}`);

        try {
          setIsDraftLoading(true);
          let draftData = null;

          if (savedDraftId) {
            // Try loading with specific draft ID from localStorage
            draftData = await formSubmissionService.loadDraft({ draftId: savedDraftId });
          }

          // If localStorage draft not found or failed, try finding draft by form ID
          // This handles: logged-in users on different devices, after logout/login, or cleared localStorage
          if (!draftData) {
            draftData = await formSubmissionService.loadDraft({ formId: formData.id });

            // If found, cache the draft ID for future fast loads
            if (draftData && draftData.formId === formData.id) {
              // Extract draft ID from backend if available (would need to be returned)
              // For now, just let it work without caching
            }
          }

          if (draftData && draftData.formId === formData.id) {
            // Fix: Handle malformed data that comes as array with nested JSON strings
            let parsedData: any = draftData.data;

            // If data is an array, try to parse it
            if (Array.isArray(parsedData)) {
              try {
                // Try to parse the first element if it's a string
                if (parsedData.length > 0 && typeof parsedData[0] === 'string') {
                  parsedData = JSON.parse(parsedData[0]);
                }
              } catch (e) {
                console.error('Failed to parse draft array data:', e);
              }
            }

            // If still a string, try parsing again (could be double-encoded)
            while (typeof parsedData === 'string') {
              try {
                parsedData = JSON.parse(parsedData);
              } catch (e) {
                console.error('Failed to parse draft string data:', e);
                break; // Exit loop on parse error
              }
            }

            // Final check: ensure we have an object
            if (parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)) {
              setFormData(parsedData);
              setDraftId(savedDraftId || 'backend-found');
              draftLoaded = true; // Mark as loaded
            }
          }
        } catch (error) {
          console.error('Failed to load draft:', error);
          // Clear invalid draft ID from localStorage
          if (savedDraftId) {
            localStorage.removeItem(`easyforms_draft_${formData.id}`);
          }
        } finally {
          setIsDraftLoading(false);
        }
      }

      // Initialize form data with default values only if draft wasn't loaded
      // Skip if form has already been submitted (sections won't be available)
      if (!draftLoaded && !formData.alreadySubmitted && formData.sections && Array.isArray(formData.sections)) {
        const initialData: Record<string, any> = {};
        formData.sections.forEach(section => {
          section.rows.forEach(row => {
            row.fields.forEach(field => {
              if (field.config.defaultValue !== undefined) {
                initialData[field.id] = field.config.defaultValue;
              }
            });
          });
        });
        setFormData(initialData);
      }
    } catch (err) {
      console.error('Failed to load form:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to load form');
      loadedFormIdRef.current = null;
    } finally {
      setIsLoading(false);
      loadingRef.current = null;
    }
  }, []);

  // Get visible fields based on conditional logic
  const getVisibleFields = useCallback((fields: FormField[]): FormField[] => {
    if (!form?.conditionalRules || form.conditionalRules.length === 0) {
      return fields;
    }

    return fields.filter(field => {
      // Find rules that target this field
      const rules = form.conditionalRules.filter(rule =>
        rule.actions.some(action =>
          action.type === 'hide_field' &&
          action.targetFieldNames?.includes(field.id)
        )
      );

      if (rules.length === 0) return true;

      // If any hide rule is met, hide the field
      const shouldHide = evaluateConditions(rules, formData);
      return !shouldHide;
    });
  }, [form, formData]);

  // Calculate pages based on form structure
  const pages = useMemo((): FormPageData[] => {
    if (!form) {
      console.log('No form data available for pages calculation');
      return [];
    }

    // If form has already been submitted, return empty pages
    if (form.alreadySubmitted) {
      return [];
    }

    const isMultiPage = form.settings.enableMultiPage;

    // Ensure sections exist and is an array
    if (!form.sections || !Array.isArray(form.sections)) {
      console.error('Form sections is not an array:', form.sections);
      return [];
    }

    if (isMultiPage) {
      // Each section is a page
      return form.sections.map(section => {
        const allFields: FormField[] = [];

        if (section.rows && Array.isArray(section.rows)) {
          section.rows.forEach(row => {
            if (row.fields && Array.isArray(row.fields)) {
              allFields.push(...row.fields);
            }
          });
        }

        const visibleFields = getVisibleFields(allFields);

        return {
          sectionId: section.id,
          title: section.title || 'Untitled Page',
          description: section.description,
          fields: visibleFields,
          isComplete: visibleFields.every(field => {
            if (!field.config.required) return true;
            const value = formData[field.id];
            return value !== undefined && value !== null && value !== '';
          }),
          errors: visibleFields
            .map(field => errors[field.id])
            .filter(Boolean) as FormValidationError[]
        };
      });
    } else {
      // Single page - all sections combined
      const allFields: FormField[] = [];
      form.sections.forEach(section => {
        if (section.rows && Array.isArray(section.rows)) {
          section.rows.forEach(row => {
            if (row.fields && Array.isArray(row.fields)) {
              allFields.push(...row.fields);
            }
          });
        }
      });

      const visibleFields = getVisibleFields(allFields);

      return [{
        sectionId: 'all',
        title: form.title,
        description: form.description,
        fields: visibleFields,
        isComplete: visibleFields.every(field => {
          if (!field.config.required) return true;
          const value = formData[field.id];
          return value !== undefined && value !== null && value !== '';
        }),
        errors: visibleFields
          .map(field => errors[field.id])
          .filter(Boolean) as FormValidationError[]
      }];
    }
  }, [form, formData, errors, getVisibleFields]);

  // Calculate progress
  const progress = useMemo((): FormProgress => {
    const totalPages = pages.length;
    const completedPages = pages.filter(p => p.isComplete).length;
    
    return {
      currentPage,
      totalPages,
      completedPages,
      percentage: totalPages > 0 ? Math.round((completedPages / totalPages) * 100) : 0,
      visitedPages
    };
  }, [currentPage, pages, visitedPages]);

  // Calculate navigation state
  const navigation = useMemo((): FormNavigationState => {
    return {
      canGoNext: currentPage < pages.length - 1,
      canGoPrevious: currentPage > 0,
      isFirstPage: currentPage === 0,
      isLastPage: currentPage === pages.length - 1
    };
  }, [currentPage, pages.length]);

  // Validate single field using ValidationService
  const validateField = useCallback(async (fieldId: string, currentFormData?: Record<string, any>): Promise<boolean> => {
    const field = fieldMap.get(fieldId);
    if (!field) {
      console.warn(`Field ${fieldId} not found for validation`);
      return false;
    }

    // Skip validation for hidden fields
    if (conditionalLogic.hiddenFields.has(fieldId)) {
      // Clear any existing errors for hidden fields
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
      return true;
    }

    // Use provided formData or fall back to current state
    const dataToValidate = currentFormData || formData;
    const value = dataToValidate[fieldId];
    const error = await ValidationService.validateField(field, value, dataToValidate);

    if (error) {
      setErrors(prev => ({ ...prev, [fieldId]: error }));
      return false;
    }

    // Clear error if validation passed
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldId];
      return newErrors;
    });

    return true;
  }, [fieldMap, formData, conditionalLogic.hiddenFields]);

  // Set field value
  const setFieldValue = useCallback((fieldId: string, value: any) => {
    // Get field for validation check
    const field = fieldMap.get(fieldId);
    const needsImmediateValidation = field?.config.required && Array.isArray(value) && value.length === 0;

    // Use functional update to avoid stale closure
    setFormData(prevFormData => {
      const newFormData = { ...prevFormData, [fieldId]: value };

      // If immediate validation needed, trigger it asynchronously with new data
      if (needsImmediateValidation) {
        setTimeout(() => validateField(fieldId, newFormData), 0);
      }

      return newFormData;
    });

    // For other cases, clear error for this field when value changes
    if (!needsImmediateValidation) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  }, [fieldMap, validateField]);

  // Validate entire page using ValidationService
  const validatePage = useCallback(async (pageIndex: number): Promise<boolean> => {
    if (!pages[pageIndex]) {
      console.warn(`Page ${pageIndex} not found for validation`);
      return false;
    }

    const page = pages[pageIndex];

    // Filter out hidden fields from validation
    const visibleFields = page.fields.filter(field => !conditionalLogic.hiddenFields.has(field.id));

    // Validate only visible fields on the page
    // Payment fields will handle their own validation internally
    const pageErrors = await ValidationService.validateFields(visibleFields, formData);

    // Update errors state - clear old errors for this page, add new ones
    setErrors(prev => {
      const newErrors = { ...prev };

      // Clear errors for all fields on this page (including hidden ones)
      page.fields.forEach(field => {
        delete newErrors[field.id];
      });

      // Add new validation errors (only for visible fields)
      return { ...newErrors, ...pageErrors };
    });

    return Object.keys(pageErrors).length === 0;
  }, [pages, formData, conditionalLogic.hiddenFields]);

  // Navigate to page
  const goToPage = useCallback((pageIndex: number) => {
    if (pageIndex >= 0 && pageIndex < pages.length) {
      setCurrentPage(pageIndex);
      setVisitedPages(prev => new Set([...prev, pageIndex]));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [pages.length]);

  // Save draft
  const saveDraft = useCallback(async () => {
    if (!form) return;

    try {
      setIsDraftSaving(true);
      const result = await formSubmissionService.saveDraft(form.id, formData);
      if (result.success && result.draftId) {
        setDraftId(result.draftId);
        // Store draft ID in localStorage for retrieval
        localStorage.setItem(`easyforms_draft_${form.id}`, result.draftId);
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
    } finally {
      setIsDraftSaving(false);
    }
  }, [form, formData]);

  // Next page
  const nextPage = useCallback(async () => {
    const isValid = await validatePage(currentPage);
    if (isValid && navigation.canGoNext) {
      // Auto-save draft before moving to next page if Save & Continue is enabled
      if (form?.settings?.allowSaveAndContinue) {
        await saveDraft();
      }
      goToPage(currentPage + 1);
    } else if (!isValid) {
      // Scroll to the error alert (or first error field) on the current page
      // Use requestAnimationFrame to ensure DOM has updated with all errors
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // First try to scroll to the error alert which shows all errors
          const errorAlert = document.getElementById('form-errors-alert');
          if (errorAlert) {
            errorAlert.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          } else {
            // Fallback to first error field if alert not found
            const firstErrorElement = document.querySelector('[data-error="true"]');
            if (firstErrorElement) {
              firstErrorElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }
        });
      });
    }
  }, [currentPage, navigation.canGoNext, validatePage, goToPage, form?.settings?.allowSaveAndContinue, saveDraft]);

  // Previous page
  const previousPage = useCallback(() => {
    if (navigation.canGoPrevious) {
      goToPage(currentPage - 1);
    }
  }, [currentPage, navigation.canGoPrevious, goToPage]);

  // Submit form
  const submitForm = useCallback(async () => {
    if (!form || !startTime) return;

    try {
      setIsSubmitting(true);
      setLoadError(null);

      // Handle reCAPTCHA Enterprise token generation BEFORE validation
      const recaptchaEnterpriseSiteKey = (window as any).__recaptchaEnterpriseSiteKey;
      const recaptchaEnterpriseAction = (window as any).__recaptchaEnterpriseAction || 'submit';
      if (recaptchaEnterpriseSiteKey) {
        try {
          // Wait for grecaptcha.enterprise to load (up to 5 seconds)
          let attempts = 0;
          while (!(window as any).grecaptcha?.enterprise && attempts < 50) {
            await new Promise(resolve => setTimeout(resolve, 100));
            attempts++;
          }

          if ((window as any).grecaptcha?.enterprise) {
            const token = await (window as any).grecaptcha.enterprise.execute(
              recaptchaEnterpriseSiteKey,
              { action: recaptchaEnterpriseAction }
            );

            // Find CAPTCHA field - log all fields for debugging
            const allFields = Array.from(fieldMap.values());
            const captchaField = allFields.find(
              f => f.type === 'captcha' && (f.config as any)?.captchaType === 'recaptcha-enterprise'
            );

            // Also try finding any captcha field as fallback
            const anyCaptchaField = allFields.find(f => f.type === 'captcha');

            if (anyCaptchaField) {
              console.log('[reCAPTCHA] CAPTCHA field details:', {
                id: anyCaptchaField.id,
                type: anyCaptchaField.type,
                captchaType: (anyCaptchaField.config as any)?.captchaType
              });
            }

            // Use the found field (prefer Enterprise, fallback to any CAPTCHA)
            const fieldToUse = captchaField || anyCaptchaField;

            if (fieldToUse && token) {
              // CRITICAL: Update formData directly and synchronously BEFORE validation
              // This ensures the token is available when validatePage runs
              formData[fieldToUse.id] = token;

              // Also update React state for consistency
              setFormData(prev => ({
                ...prev,
                [fieldToUse.id]: token
              }));
            } else if (!fieldToUse) {
              console.error('[reCAPTCHA] CAPTCHA field not found in form!');
            } else if (!token) {
              console.error('[reCAPTCHA] Token is empty!');
            }
          } else {
            console.error('[reCAPTCHA] grecaptcha.enterprise not loaded after 5 seconds');
          }
        } catch (error) {
          console.error('[reCAPTCHA] Token generation failed:', error);
        }
      } else {
        console.log('[reCAPTCHA] No reCAPTCHA Enterprise field detected');
      }

      // Validate all pages
      const allValid = await Promise.all(
        pages.map((_, index) => validatePage(index))
      );

      if (!allValid.every(v => v)) {
        // Validation failed - scroll to errors
        requestAnimationFrame(() => {
          const errorAlert = document.getElementById('form-errors-alert');
          if (errorAlert) {
            errorAlert.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest'
            });
          } else {
            const firstErrorElement = document.querySelector('[data-error="true"]');
            if (firstErrorElement) {
              firstErrorElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
              });
            }
          }
        });

        setIsSubmitting(false);
        return;
      }

      // Check payment field validity BEFORE submission
      // Check for any payment gateway (Stripe or PayPal)
      const paymentFieldData = Object.entries(formData).find(
        ([, value]: [string, any]) => (value?.gateway === 'stripe' || value?.gateway === 'paypal') && value?._fieldId
      );

      if (paymentFieldData) {
        const [paymentFieldId, paymentValue] = paymentFieldData as [string, any];

        // Call validation if available
        if (typeof paymentValue.validate === 'function') {
          const validationResult = paymentValue.validate();

          if (!validationResult.valid) {
            // Payment not ready - show error and stop
            const field = fieldMap.get(paymentFieldId);
            setErrors(prev => ({
              ...prev,
              [paymentFieldId]: {
                fieldId: paymentFieldId,
                fieldName: field?.config?.fieldName || field?.config?.label || 'Payment',
                message: validationResult.message || 'Please complete payment information',
              }
            }));

            // Scroll to payment field
            setTimeout(() => {
              const paymentFieldElement = document.querySelector(`[data-field-id="${paymentFieldId}"]`);
              if (paymentFieldElement) {
                paymentFieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);

            setIsSubmitting(false);
            return;
          }
        }
      }


      // Log formData before creating submission
      console.log('[DEBUG] FormData before submission:', formData);

      const submissionData: FormSubmissionData = {
        formId: form.id,
        fields: formData,
        metadata: {
          startTime,
          endTime: new Date(),
          timeTaken: Math.round((new Date().getTime() - startTime.getTime()) / 1000),
          userAgent: navigator.userAgent,
          referrer: document.referrer
        }
      };

      console.log('[DEBUG] Submission data fields:', submissionData.fields);
      console.log('[DEBUG] Full submission data:', submissionData);

      // Use paymentFieldData from validation above
      if (paymentFieldData) {
        // Handle payment submission
        const [, paymentValue] = paymentFieldData as [string, any];
        const fieldId = paymentValue._fieldId;
        const gateway = paymentValue.gateway;

        if (gateway === 'paypal') {
          // PayPal payment flow
          // Submit form to backend - the PayPalHandler will create the order and return redirect URL
          const response = await formSubmissionService.submitForm(submissionData);

          // Check if we got a redirect URL from PayPal
          if (response.redirect) {
            // Redirect to PayPal for payment
            window.location.href = response.redirect;
            // Don't set submitting to false - we're redirecting
            return;
          } else {
            // No redirect URL - payment may have failed or wasn't properly configured
            setSubmissionResponse(response);
            setSubmissionComplete(true);

            if (form.settings?.allowMultipleSubmissions === false) {
              markFormAsSubmitted(form.id);
            }
          }

        } else if (gateway === 'stripe') {
          // Stripe payment flow (existing code)
          // Get card element from window storage
          const stripeElements = (window as any).__stripePaymentElements;
          const stripeData = stripeElements?.[fieldId];

          if (!stripeData?.cardElement || !stripeData?.stripe) {
            throw new Error('Stripe payment elements not found');
          }

          const { cardElement } = stripeData;

          // Submit to backend to create payment intent
          let response;
          if (!pendingSubmissionId) {
            response = await formSubmissionService.submitForm(submissionData);
            if (response.submissionId) {
              setPendingSubmissionId(response.submissionId);
            }
          } else {
            response = await formSubmissionService.submitForm(submissionData);
          }

          // Confirm payment with Stripe
          if (response.payment?.requiresAction && response.payment?.clientSecret) {
            const { stripe: stripeInstance } = stripeData;

            const { error, paymentIntent } = await stripeInstance.confirmCardPayment(
              response.payment.clientSecret,
              {
                payment_method: {
                  card: cardElement,
                  billing_details: {
                    name: paymentValue.cardholderName || undefined,
                    address: paymentValue.billingAddress ? {
                      line1: paymentValue.billingAddress.street,
                      city: paymentValue.billingAddress.city,
                      state: paymentValue.billingAddress.state,
                      postal_code: paymentValue.billingAddress.zip,
                      country: paymentValue.billingAddress.country,
                    } : undefined,
                  },
                },
              }
            );

            if (error) {
              // Payment failed - set error and stop
              const paymentFieldId = Object.keys(formData).find(
                key => formData[key]?.gateway === 'stripe'
              );

              if (paymentFieldId) {
                const field = fieldMap.get(paymentFieldId);
                setErrors(prev => ({
                  ...prev,
                  [paymentFieldId]: {
                    fieldId: paymentFieldId,
                    fieldName: field?.config?.fieldName || field?.config?.label || 'Payment',
                    message: error.message || 'Payment failed. Please check your card details.',
                  }
                }));

                setTimeout(() => {
                  const paymentFieldElement = document.querySelector(`[data-field-id="${paymentFieldId}"]`);
                  paymentFieldElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
              }

              setIsSubmitting(false);
              return;
            }

            // Payment successful - update backend
            if (paymentIntent?.status) {
              try {
                await formSubmissionService.updatePaymentStatus(
                  response.submissionId,
                  paymentIntent.status,
                  paymentIntent.id
                );
                console.log('Payment status updated to:', paymentIntent.status);
              } catch (updateError) {
                console.error('Failed to update payment status:', updateError);
                // Don't fail the entire submission, just log the error
              }
            }

            response.paymentStatus = paymentIntent?.status;
            setPendingSubmissionId(null);
          }

          setSubmissionResponse(response);
          setSubmissionComplete(true);

          // Clear saved draft after successful submission
          if (form.settings?.allowSaveAndContinue) {
            localStorage.removeItem(`easyforms_draft_${form.id}`);
            setDraftId(null);
          }

          if (form.settings?.allowMultipleSubmissions === false) {
            markFormAsSubmitted(form.id);
          }

          if (response.results) {
            setQuizResults(response.results);
          }
        }

      } else {
        // No payment - regular submission
        const response = await formSubmissionService.submitForm(submissionData);

        // Check if response contains validation errors from backend (e.g., CAPTCHA failure)
        if (!response.success && response.validationErrors) {
          // Convert validation errors to field errors format
          const newErrors: Record<string, FormValidationError> = {};

          for (const [fieldId, message] of Object.entries(response.validationErrors)) {
            if (fieldId === '_form') {
              // General form error - try to find the CAPTCHA field
              const captchaField = Array.from(fieldMap.values()).find(f => f.type === 'captcha');
              if (captchaField) {
                newErrors[captchaField.id] = {
                  fieldId: captchaField.id,
                  fieldName: captchaField.config?.label || 'Security Verification',
                  message: message as string,
                };
              } else {
                // If no CAPTCHA field found, show as general error
                setLoadError(message as string);
              }
            } else {
              const field = fieldMap.get(fieldId);
              newErrors[fieldId] = {
                fieldId,
                fieldName: field?.config?.label || field?.config?.fieldName || 'Field',
                message: message as string,
              };
            }
          }

          setErrors(newErrors);
          setIsSubmitting(false);

          // Scroll to first error
          const firstErrorFieldId = Object.keys(newErrors)[0];
          if (firstErrorFieldId) {
            setTimeout(() => {
              const errorElement = document.querySelector(`[data-field-id="${firstErrorFieldId}"]`);
              errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 100);
          }

          return;
        }

        setSubmissionResponse(response);
        setSubmissionComplete(true);

        if (form.settings?.allowMultipleSubmissions === false) {
          markFormAsSubmitted(form.id);
        }

        if (response.results) {
          setQuizResults(response.results);
        }
      }

      // Scroll to top
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      // Redirect if specified
      if (submissionResponse?.redirectUrl) {
        setTimeout(() => {
          window.location.href = submissionResponse.redirectUrl!;
        }, 2000);
      }

    } catch (err) {
      console.error('Form submission failed:', err);
      setLoadError(err instanceof Error ? err.message : 'Failed to submit form');
    } finally {
      setIsSubmitting(false);
    }
  }, [form, formData, startTime, pages, validatePage, fieldMap, submissionResponse, pendingSubmissionId]);



  // Reset form
  const resetForm = useCallback(() => {
    setFormData({});
    setErrors({});
    setCurrentPage(0);
    setVisitedPages(new Set([0]));
    setSubmissionComplete(false);
    setSubmissionResponse(null);
    setQuizResults(null);
    setStartTime(new Date());
  }, []);

  // Get specific integration by provider
  const getIntegration = useCallback((provider: string): Integration | null => {
    return integrations.find(
      (integration) => integration.provider === provider && integration.status === 'active'
    ) || null;
  }, [integrations]);

  // Get integration config by provider
  const getIntegrationConfig = useCallback((provider: string): any | null => {
    const integration = getIntegration(provider);
    if (!integration) return null;

    // Parse config if it's a string
    if (typeof integration.config === 'string') {
      try {
        return JSON.parse(integration.config);
      } catch {
        return null;
      }
    }

    return integration.config;
  }, [getIntegration]);


  const value: FormSubmissionContextValue = {
    form,
    formData,
    errors,
    currentPage,
    pages,
    progress,
    navigation,
    isSubmitting,
    isLoading,
    loadError,
    submissionComplete,
    submissionResponse,
    quizResults,
    startTime,
    integrations,
    isDraftLoading,
    isDraftSaving,
    setFieldValue,
    validateField,
    validatePage,
    goToPage,
    nextPage,
    previousPage,
    submitForm,
    saveDraft,
    resetForm,
    loadForm,
    getIntegration,
    getIntegrationConfig
  };

  return (
    <FormSubmissionContext.Provider value={value}>
      {children}
    </FormSubmissionContext.Provider>
  );
}

export function useFormSubmission() {
  const context = useContext(FormSubmissionContext);
  if (!context) {
    throw new Error('useFormSubmission must be used within FormSubmissionProvider');
  }
  return context;
}
